import LogoSpinner from '@/components/atoms/LogoSpinner';
import { Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

interface Props {
  // Backend estimate of seconds until generation completes (from the latest poll response).
  estimatedSeconds?: number | null;
  // Changes when a new generation request starts — used to reset the countdown.
  requestId?: string | null;
  // 'block' = full notice with spinner (shown in place of the recommendations);
  // 'inline' = compact tag (shown next to the heading while a stale snapshot is on screen).
  variant?: 'block' | 'inline';
}

const DEFAULT_ESTIMATE_SECONDS = 30;

const InsightsGeneratingNotice = ({ estimatedSeconds, requestId, variant = 'block' }: Props) => {
  const initialDeadline = () => Date.now() + (estimatedSeconds ?? DEFAULT_ESTIMATE_SECONDS) * 1000;
  const [deadline, setDeadline] = useState<number>(initialDeadline);
  const [now, setNow] = useState<number>(() => Date.now());

  // Re-anchor the countdown when a fresh generation kicks off (or the estimate changes).
  useEffect(() => {
    setDeadline(Date.now() + (estimatedSeconds ?? DEFAULT_ESTIMATE_SECONDS) * 1000);
  }, [requestId, estimatedSeconds]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));

  if (variant === 'inline') {
    return (
      <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'} whiteSpace={'nowrap'}>
        {remaining > 0 ? `UPDATING · ~${remaining}s` : 'UPDATING…'}
      </Text>
    );
  }

  return (
    <VStack alignItems={'center'} gap={'10px'} width={'100%'} paddingY={'12px'} marginBottom={'12px'}>
      <LogoSpinner size={'40px'} />
      <Text variant={'urbanistBold'} color={'text.mediumBlue'} textAlign={'center'}>
        Generating recommendations…
      </Text>
      <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} textAlign={'center'}>
        {remaining > 0
          ? `Usually ready in about ${remaining}s — this will refresh automatically.`
          : 'Almost done — this is taking a little longer than usual. It will refresh automatically.'}
      </Text>
    </VStack>
  );
};

export default InsightsGeneratingNotice;
